/**
 * Roving tabindex for toolbar keyboard navigation.
 *
 * Manages focus within a toolbar so only one item is tabbable (tabIndex=0)
 * while others are tabIndex=-1. Arrow keys move focus between items.
 */

import { useCallback, useRef, useEffect } from "react";

export function useRovingTabindex() {
  const toolbarRef = useRef<HTMLDivElement>(null);

  const getFocusableItems = useCallback((): HTMLElement[] => {
    if (!toolbarRef.current) return [];
    return Array.from(
      toolbarRef.current.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [role="button"]:not([aria-disabled="true"])'
      )
    );
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const items = getFocusableItems();
      if (items.length === 0) return;

      const currentIndex = items.indexOf(e.target as HTMLElement);
      if (currentIndex === -1) return;

      let nextIndex: number;

      switch (e.key) {
        case "ArrowRight":
          nextIndex = (currentIndex + 1) % items.length;
          break;
        case "ArrowLeft":
          nextIndex = (currentIndex - 1 + items.length) % items.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = items.length - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      e.stopPropagation();

      const target = items[nextIndex];
      if (!target) return;

      items.forEach((item, i) => {
        item.tabIndex = i === nextIndex ? 0 : -1;
      });
      target.focus();
    },
    [getFocusableItems]
  );

  useEffect(() => {
    const container = toolbarRef.current;
    if (!container) return;

    const initTabIndices = () => {
      const items = getFocusableItems();
      if (items.length === 0) return;
      // Preserve focus if an item is already focused within the toolbar
      const focused = document.activeElement as HTMLElement;
      const focusedIndex = items.indexOf(focused);
      items.forEach((item, i) => {
        item.tabIndex = (focusedIndex >= 0 ? i === focusedIndex : i === 0) ? 0 : -1;
      });
    };

    initTabIndices();

    const observer = new MutationObserver(initTabIndices);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled", "aria-disabled"],
    });

    return () => observer.disconnect();
  }, [getFocusableItems]);

  // When a button is clicked/focused via mouse, update tabIndex state to match
  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      const items = getFocusableItems();
      const focusedIndex = items.indexOf(e.target as HTMLElement);
      if (focusedIndex === -1) return;
      items.forEach((item, i) => {
        item.tabIndex = i === focusedIndex ? 0 : -1;
      });
    },
    [getFocusableItems]
  );

  return { toolbarRef, handleKeyDown, handleFocus };
}
